import React, { useState, useCallback } from 'react';
import {
  Modal,
  Upload,
  Button,
  notification,
  Progress,
  Typography,
  Input,
  Form,
} from 'antd';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { doFileUpload } from '@aims/shared/shared/utils';
import { generateUuid } from '@aims/shared/shared/utils';
import {
  getAttachmentUploadUrlForAdminMutation,
  saveAttachmentForAdminMutation,
  createStrategyGuideMutation,
} from './constants';

const { Text } = Typography;
const { Dragger } = Upload;
const { TextArea } = Input;

function StrategyGuideUploadModal({
  visible,
  onCancel,
  onComplete,
  contactId,
  contact,
  currentVersion,
  rejectPreviousId,
  rejectionNotes,
}) {
  const [form] = Form.useForm();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [getUploadUrl] = useMutation(getAttachmentUploadUrlForAdminMutation);
  const [saveAttachment] = useMutation(saveAttachmentForAdminMutation);
  const [createStrategyGuide] = useMutation(createStrategyGuideMutation);

  const nextVersion = currentVersion + 1;

  const handleUpload = useCallback(async () => {
    if (!file) {
      notification.error({
        message: 'Error',
        description: 'Please select a file to upload.',
      });
      return;
    }

    try {
      setUploading(true);
      const values = await form.validateFields();

      const attachmentId = generateUuid();
      const strategyGuideId = generateUuid();
      const contactName = contact?.fullName?.replace(/\s+/g, '_') || 'Contact';
      const s3Key = `strategy-guides/${contactId}/${attachmentId}/${file.name}`;

      // Get presigned upload URL
      const uploadUrlResponse = await getUploadUrl({
        variables: {
          attachment: {
            _id: attachmentId,
            s3Key,
          },
        },
      });

      if (!uploadUrlResponse.data?.getAttachmentUploadUrlForAdmin) {
        throw new Error('Failed to get upload URL');
      }

      // Upload file to S3
      setUploadProgress(0);
      await doFileUpload({
        url: uploadUrlResponse.data.getAttachmentUploadUrlForAdmin.url,
        fields: uploadUrlResponse.data.getAttachmentUploadUrlForAdmin.fields,
        file: file,
        onProgress: (event) => {
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        },
      });

      // Save attachment record
      await saveAttachment({
        variables: {
          attachment: {
            _id: attachmentId,
            filename: file.name,
            s3Key,
            attachedTo: contactId,
            tags: ['strategy-guide'],
            uploaded: true,
          },
        },
      });

      // Create strategy guide record
      const fileName =
        values.fileName || `${contactName}_StrategyGuide_v${nextVersion}.pdf`;

      const strategyGuideInput = {
        _id: strategyGuideId,
        contactId,
        fileId: attachmentId,
        fileName,
        notes: values.notes || null,
        notesText: values.notes ? values.notes.replace(/<[^>]*>/g, '') : null,
      };

      // Add rejection context if provided
      if (rejectPreviousId) {
        strategyGuideInput.rejectPreviousId = rejectPreviousId;
        strategyGuideInput.rejectionNotes = rejectionNotes;
      }

      await createStrategyGuide({
        variables: {
          strategyGuide: strategyGuideInput,
        },
      });

      notification.success({
        message: 'Success',
        description: `Strategy Guide v${nextVersion} uploaded successfully.`,
      });

      // Reset form and close modal
      form.resetFields();
      setFile(null);
      setUploadProgress(0);
      onComplete();
    } catch (err) {
      notification.error({
        message: 'Error',
        description: 'Failed to upload strategy guide. Please try again.',
      });
    } finally {
      setUploading(false);
    }
  }, [
    file,
    form,
    contactId,
    contact,
    nextVersion,
    getUploadUrl,
    saveAttachment,
    createStrategyGuide,
    onComplete,
    rejectPreviousId,
    rejectionNotes,
  ]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    setFile(null);
    setUploadProgress(0);
    onCancel();
  }, [form, onCancel]);

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.pdf',
    beforeUpload: (f) => {
      if (f.type !== 'application/pdf') {
        notification.error({
          message: 'Error',
          description: 'Only PDF files are allowed.',
        });
        return Upload.LIST_IGNORE;
      }
      setFile(f);
      return false; // Prevent auto-upload
    },
    onRemove: () => {
      setFile(null);
    },
    fileList: file ? [file] : [],
  };

  return (
    <Modal
      title={`Upload Strategy Guide (v${nextVersion})`}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={uploading}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          icon={<UploadOutlined />}
          onClick={handleUpload}
          loading={uploading}
          disabled={!file}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>,
      ]}
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item label="File" required>
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag PDF file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Only PDF files are supported. This will be version {nextVersion}.
            </p>
          </Dragger>
        </Form.Item>

        {uploading && (
          <div style={{ marginBottom: 16 }}>
            <Progress percent={uploadProgress} status="active" />
            <Text type="secondary">Uploading...</Text>
          </div>
        )}

        <Form.Item
          name="fileName"
          label="Display Name (Optional)"
          help="Leave blank to auto-generate based on contact name"
        >
          <Input
            placeholder={`${
              contact?.fullName?.replace(/\s+/g, '_') || 'Contact'
            }_StrategyGuide_v${nextVersion}.pdf`}
          />
        </Form.Item>

        <Form.Item name="notes" label="Notes (Optional)">
          <TextArea
            rows={4}
            placeholder="Add any notes about this version..."
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default StrategyGuideUploadModal;
